/* eslint-disable @stylistic/max-len */
import React, {type RefObject} from 'react';

interface IconProps {
    /**
     * A string representing the CSS class to be applied to the LogoO2Icon element to override its default styling.
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     */
    color1?: string;
    /**
     * A number or string representing the height of the LogoO2Icon element.
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement | null>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     */
    testId?: string;
    /**
     * A number or string representing the height of the LogoO2Icon element.
     */
    width?: number | string;
}

/**
 * The `LogoO2` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `LogoO2Props` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the LogoO2Icon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the LogoO2Icon element.
 * @param props.ref       A reference to the SVG element, allowing for direct manipulation or access to the DOM node.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the LogoO2Icon element.
 * @returns A React element representing the `LogoO2` component with the specified properties.
 *
 * @example
 * ```tsx
 * const LogoO2Component = <LogoO2 className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const LogoO2 = ({
    className = '',
    color1 = '#fff',
    height = 32,
    ref,
    testId = 'LogoO2',
    width = 32
}: IconProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 32 32"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path
            d="M1.188 13.75c0-6.938 4.688-12 11.688-12 6.938 0 11.625 5.063 11.625 12 0 6.563-4.75 12-11.625 12-6.938 0-11.688-5.438-11.688-12zM5.688 13.75c0 4.125 2.188 8.563 7.188 8.563 4.938 0 7.125-4.438 7.125-8.563 0-4.313-2.625-8.563-7.125-8.563-4.563 0-7.188 4.25-7.188 8.563zM30.813 30.25h-6.75v-1.75c0.938-0.875 4.375-3.75 4.375-5.625 0-0.938-0.813-1.375-1.625-1.375-0.875 0-1.688 0.375-2.375 0.813l-0.125-1.688c0.875-0.438 1.938-0.688 3-0.688 1.938 0 3.188 0.938 3.188 2.938 0 2.125-2.188 4.125-3.813 5.75h4.125z"
            fill={color1}
        />
    </svg>
);

LogoO2.displayName = 'LogoO2';